import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import HandymanIcon from "@mui/icons-material/Handyman";
import ComputerIcon from "@mui/icons-material/Computer";
import CampaignIcon from "@mui/icons-material/Campaign";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { toast } from "react-toastify";

import {
  API_get_assignment_settings,
  API_update_assignment_settings,
} from "../../../constants";
import {
  getRightworkAPI,
  postRightworkAPI,
} from "../requests/utilities/requests";

const PERMISSION_LABELS = {
  company_employee: "Employee",
  company_manager: "Manager",
  company_admin: "Admin",
  rightwork_internal: "Rightwork",
};

const CATEGORY_META = {
  maintenance: { label: "Maintenance", icon: HandymanIcon },
  it: { label: "IT", icon: ComputerIcon },
  marketing: { label: "Marketing", icon: CampaignIcon },
  menu: { label: "Menu", icon: RestaurantMenuIcon },
};

// Compare two Sets for equality.
function setsEqual(a, b) {
  if (!a || !b) return false;
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
}

const checkboxSx = { color: "#6d28d9", "&.Mui-checked": { color: "#6d28d9" } };


function ColumnHeader({ label, onAll, onNone, disabled }) {
  return (
    <div className="w-[76px] flex flex-col items-center">
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      <div className="flex gap-1 text-[10px] mt-0.5">
        <button
          type="button"
          onClick={onAll}
          disabled={disabled}
          className="text-violet-700 hover:text-violet-900 disabled:text-slate-300"
        >
          All
        </button>
        <span className="text-slate-300">/</span>
        <button
          type="button"
          onClick={onNone}
          disabled={disabled}
          className="text-violet-700 hover:text-violet-900 disabled:text-slate-300"
        >
          None
        </button>
      </div>
    </div>
  );
}


function CandidateRow({ candidate, isAssignable, isApprover, onToggle }) {
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-100 text-violet-700 text-xs font-semibold">
        {candidate.initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate">
          {candidate.full_name}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {PERMISSION_LABELS[candidate.permission_type] || candidate.permission_type}
        </div>
      </div>
      <div className="w-[76px] flex justify-center">
        <Checkbox
          checked={isAssignable}
          onChange={() => onToggle(candidate.user_id, "assignable")}
          size="small"
          sx={checkboxSx}
        />
      </div>
      <div className="w-[76px] flex justify-center">
        <Checkbox
          checked={isApprover}
          onChange={() => onToggle(candidate.user_id, "approver")}
          size="small"
          sx={checkboxSx}
        />
      </div>
    </div>
  );
}


export default function TicketAssignmentSettingsModal({
  open,
  onClose,
  storeId,
  accessibleCategories,
  onSaved,
}) {
  const tabs = useMemo(
    () =>
      (accessibleCategories || []).filter((c) => CATEGORY_META[c.value]),
    [accessibleCategories],
  );

  const [activeCategory, setActiveCategory] = useState(tabs[0]?.value || "maintenance");
  const [candidatesByCategory, setCandidatesByCategory] = useState({});
  const [assignableByCategory, setAssignableByCategory] = useState({});
  const [originalAssignableByCategory, setOriginalAssignableByCategory] = useState({});
  const [approverByCategory, setApproverByCategory] = useState({});
  const [originalApproverByCategory, setOriginalApproverByCategory] = useState({});
  const [loadingByCategory, setLoadingByCategory] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset state when the modal transitions to open. We intentionally only
  // depend on `open` — `tabs` is read inside but we don't want a re-render
  // of the parent (which can change the `tabs` reference) to wipe edits.
  useEffect(() => {
    if (!open) return;
    setActiveCategory(tabs[0]?.value || "maintenance");
    setCandidatesByCategory({});
    setAssignableByCategory({});
    setOriginalAssignableByCategory({});
    setApproverByCategory({});
    setOriginalApproverByCategory({});
    setLoadingByCategory({});
    setSearchQuery("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fetch candidates for the active category whenever it changes (and isn't loaded)
  const fetchCategory = useCallback(
    async (category) => {
      if (!storeId || !category) return;
      setLoadingByCategory((prev) => ({ ...prev, [category]: true }));
      try {
        const res = await getRightworkAPI(API_get_assignment_settings, {
          store_id: storeId,
          category,
        });
        const candidates = res?.data?.redux?.payload?.candidates || [];
        const assignableSet = new Set(
          candidates.filter((c) => c.is_assignable).map((c) => c.user_id),
        );
        const approverSet = new Set(
          candidates.filter((c) => c.is_approver).map((c) => c.user_id),
        );
        setCandidatesByCategory((prev) => ({ ...prev, [category]: candidates }));
        setAssignableByCategory((prev) => ({ ...prev, [category]: assignableSet }));
        setOriginalAssignableByCategory((prev) => ({
          ...prev,
          [category]: new Set(assignableSet),
        }));
        setApproverByCategory((prev) => ({ ...prev, [category]: approverSet }));
        setOriginalApproverByCategory((prev) => ({
          ...prev,
          [category]: new Set(approverSet),
        }));
      } catch (error) {
        toast.error(
          error?.response?.data?.system?.message || "Failed to load assignment settings.",
          { autoClose: 4000 },
        );
      } finally {
        setLoadingByCategory((prev) => ({ ...prev, [category]: false }));
      }
    },
    [storeId],
  );

  useEffect(() => {
    if (!open || !activeCategory) return;
    if (candidatesByCategory[activeCategory] !== undefined) return;
    fetchCategory(activeCategory);
  }, [open, activeCategory, candidatesByCategory, fetchCategory]);

  const candidates = candidatesByCategory[activeCategory] || [];
  const assignableSet = assignableByCategory[activeCategory] || new Set();
  const approverSet = approverByCategory[activeCategory] || new Set();
  const originalAssignableSet = originalAssignableByCategory[activeCategory];
  const originalApproverSet = originalApproverByCategory[activeCategory];
  const isLoading = !!loadingByCategory[activeCategory];

  const hasChanges = useMemo(() => {
    if (!originalAssignableSet || !originalApproverSet) return false;
    return (
      !setsEqual(assignableSet, originalAssignableSet) ||
      !setsEqual(approverSet, originalApproverSet)
    );
  }, [assignableSet, originalAssignableSet, approverSet, originalApproverSet]);

  const filteredCandidates = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) => c.full_name.toLowerCase().includes(q));
  }, [candidates, searchQuery]);

  // setter selects which per-category state map a column writes to.
  const columnSetter = (column) =>
    column === "approver" ? setApproverByCategory : setAssignableByCategory;

  const handleToggle = (userId, column) => {
    columnSetter(column)((prev) => {
      const current = new Set(prev[activeCategory] || []);
      if (current.has(userId)) current.delete(userId);
      else current.add(userId);
      return { ...prev, [activeCategory]: current };
    });
  };

  const handleSelectAll = (column) => {
    columnSetter(column)((prev) => ({
      ...prev,
      [activeCategory]: new Set(candidates.map((c) => c.user_id)),
    }));
  };

  const handleSelectNone = (column) => {
    columnSetter(column)((prev) => ({
      ...prev,
      [activeCategory]: new Set(),
    }));
  };

  const handleSave = async () => {
    if (!storeId || !activeCategory) return;
    setSubmitting(true);
    try {
      await postRightworkAPI(API_update_assignment_settings, {
        store_id: storeId,
        category: activeCategory,
        assignable_user_ids: JSON.stringify(Array.from(assignableSet)),
        approver_user_ids: JSON.stringify(Array.from(approverSet)),
      });
      toast.success("Assignment settings updated.", { autoClose: 3000 });
      // Mark current state as the new baseline so Save disables until further edits.
      setOriginalAssignableByCategory((prev) => ({
        ...prev,
        [activeCategory]: new Set(assignableSet),
      }));
      setOriginalApproverByCategory((prev) => ({
        ...prev,
        [activeCategory]: new Set(approverSet),
      }));
      onSaved?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Failed to update assignment settings.",
        { autoClose: 4000 },
      );
    } finally {
      setSubmitting(false);
    }
  };

  const bulkDisabled = isLoading || candidates.length === 0;

  return (
    <Modal open={open} onClose={submitting ? undefined : onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          height: "min(720px, 90vh)",
          bgcolor: "white",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Ticket assignment & approval settings
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Choose who appears in the Assigned To and Approver pickers. Applies company-wide.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* Category tabs (only when more than one) */}
        {tabs.length > 1 && (
          <div className="flex items-center gap-4 px-5 border-b border-slate-200">
            {tabs.map((tab) => {
              const meta = CATEGORY_META[tab.value];
              const Icon = meta.icon;
              const isActive = activeCategory === tab.value;
              return (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setActiveCategory(tab.value)}
                  className={`flex items-center gap-2 py-3 text-sm font-medium border-b-2 ${
                    isActive
                      ? "border-violet-700 text-violet-700"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <Icon sx={{ fontSize: 16 }} />
                  {meta.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Search */}
        <div className="px-5 pt-4 pb-2">
          <div className="relative">
            <SearchIcon
              sx={{ fontSize: 18 }}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search people..."
              className="w-full h-9 pl-9 pr-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
            />
          </div>
        </div>

        {/* Column headers */}
        {candidates.length > 0 && (
          <div className="flex items-center gap-2 px-5 pb-1.5 border-b border-slate-100">
            <div className="flex-1" />
            <ColumnHeader
              label="Assignee"
              disabled={bulkDisabled}
              onAll={() => handleSelectAll("assignable")}
              onNone={() => handleSelectNone("assignable")}
            />
            <ColumnHeader
              label="Approver"
              disabled={bulkDisabled}
              onAll={() => handleSelectAll("approver")}
              onNone={() => handleSelectNone("approver")}
            />
          </div>
        )}

        {/* Candidate list */}
        <div className="flex-1 overflow-y-auto px-3 pb-4 pt-1">
          {isLoading ? (
            <div className="flex items-center justify-center h-full text-sm text-slate-500">
              Loading...
            </div>
          ) : candidates.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <p className="text-sm text-slate-700 font-medium">
                No one has access to this category yet
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Grant {CATEGORY_META[activeCategory]?.label || activeCategory} access from the team page first.
              </p>
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div className="flex items-center justify-center h-full text-sm text-slate-500">
              No matches.
            </div>
          ) : (
            <div className="flex flex-col">
              {filteredCandidates.map((c) => (
                <CandidateRow
                  key={c.user_id}
                  candidate={c}
                  isAssignable={assignableSet.has(c.user_id)}
                  isApprover={approverSet.has(c.user_id)}
                  onToggle={handleToggle}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
          <p className="text-xs text-slate-500">
            {candidates.length > 0 && (
              <>
                <span className="font-semibold text-slate-700">{assignableSet.size}</span>
                {" assignable · "}
                <span className="font-semibold text-slate-700">{approverSet.size}</span>
                {" approver"}
              </>
            )}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm text-slate-700 hover:bg-white disabled:opacity-50"
            >
              Cancel
            </button>
            <LoadingButton
              variant="contained"
              loading={submitting}
              disabled={isLoading || candidates.length === 0 || !hasChanges}
              onClick={handleSave}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                backgroundColor: "#6d28d9",
                "&:hover": { backgroundColor: "#5b21b6" },
                fontSize: 14,
                px: 2.5,
                py: 1,
              }}
            >
              Save changes
            </LoadingButton>
          </div>
        </div>
      </Box>
    </Modal>
  );
}
